import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Icon from 'components/AppIcon';

const PortfolioChart = ({ data, timeframe, totalValue, change24h, changePercent24h }) => {
  const isPositive = change24h >= 0;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-surface border border-border rounded-trading p-3 shadow-trading-modal">
          <p className="font-caption-normal text-xs text-text-secondary mb-1">{label}</p>
          <p className="font-data text-sm text-text-primary">
            ${payload[0].value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-surface border border-border rounded-trading p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-heading-semibold text-text-primary mb-2">
            Portfolio Performance
          </h3>
          <div className="flex items-center space-x-4">
            <div className="text-2xl font-heading-bold text-text-primary">
              ${totalValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div className={`flex items-center space-x-1 ${isPositive ? 'text-success' : 'text-error'}`}>
              <Icon name={isPositive ? 'TrendingUp' : 'TrendingDown'} size={16} />
              <span className="font-body-medium text-sm">
                {isPositive ? '+' : ''}${Math.abs(change24h).toLocaleString('en-US', { 
                  minimumFractionDigits: 2, 
                  maximumFractionDigits: 2 
                })}
              </span>
              <span className="font-body-medium text-sm">
                ({isPositive ? '+' : ''}{changePercent24h.toFixed(2)}%)
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Icon name="TrendingUp" size={16} className="text-text-secondary" />
          <span className="font-caption-normal text-xs text-text-secondary">
            {timeframe.toUpperCase()} Performance
          </span>
        </div>
      </div>

      <div className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="date" 
              stroke="var(--color-text-secondary)"
              fontSize={12}
              tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            />
            <YAxis 
              stroke="var(--color-text-secondary)"
              fontSize={12}
              tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line 
              type="monotone" 
              dataKey="value" 
              stroke={isPositive ? "var(--color-success)" : "var(--color-error)"}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, fill: isPositive ? "var(--color-success)" : "var(--color-error)" }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default PortfolioChart;